import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { tap } from 'rxjs/operators';

import { Permission, SecurityScope } from '../../auth/auth.model';
import { AuthService } from '../../auth/auth.service';
import { PopUpService } from '../../coaching-observation/experimental.pop-up';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { SearchUserContainerComponent } from '../../user/search-user.container.component';
import { SearchUserDialog } from '../../user/search-user.dialog';
import { WidgetComponentConfig } from '../../widget/widget.component';
import { CMPS_WIDGET_COMPONENT_CONFIG } from '../../widget/widget.metadata';
import { TranslationKeyMap } from '../coaching-insight.i18n';
import { FLOAPerson } from '../oa/models/floa.model';
import { StickyHeaderService } from '../stickyheader/stickyheader.service';

import {
    clearState,
    reload,
    selectKpi,
    selectPeriodType,
    selectUser,
} from './insight-widget.action';
import coachingInsightReducer from './insight-widget.reducer';
import { RootState } from './insight-widget.state';
import { KpiType } from './model/kpi-type.model';
import { SelectionChangeEvent } from './model/selection-change-event.model';

@Component({
    selector: '[cmpsWidgetCoachingInsightNext]',
    templateUrl: './insight-widget.component.html',
    host: {
        'data-e2e': 'component-coaching-insight',
    },
    providers: [
        // yes this needs to be provided at the component level
        StickyHeaderService,
    ],
})
@TakeUntilDestroy()
@UseStore('coachingInsight.root', coachingInsightReducer.coachingInsight.root)
export class CoachingInsightWidgetComponentNext
    implements OnInit, OnDestroy, StateToProps<RootState>, TakeUntilDestroy
{
    state: RootState;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _KpiType = KpiType;
    /** @private */ _userImage: SafeResourceUrl;
    /** @private */ _oaUser: FLOAPerson;
    /** @private */ _hideWidget: boolean;

    constructor(
        private readonly _domSanitizer: DomSanitizer,
        private readonly _authService: AuthService,
        @Optional()
        @Inject(CMPS_WIDGET_COMPONENT_CONFIG)
        public compConfig: WidgetComponentConfig,
        private readonly _popUpService: PopUpService,
        private readonly _userSearchDialog: SearchUserDialog,
        private readonly _activatedRoute: ActivatedRoute,
    ) {}

    ngOnInit(): void {
        if (this.state.authUserId == null) {
            useDispatcher(this, clearState({}));
        }

        this._hideWidget = !this._authService.user.hasPermission(
            Permission.VIEW,
            SecurityScope.COACHING,
        );
        let coachingInsightUserId = parseInt(
            sessionStorage.getItem('coachingInsight-userId'),
        );
        /** sessionStorage.removeItem('coachingInsight-userId'); // clear session item every load */

        // TODO revise. this is temporary means of flushing tabs after auth user has changed
        if (
            this.state.authUserId !== this._authService.user.id ||
            coachingInsightUserId == null ||
            isNaN(coachingInsightUserId)
        ) {
            coachingInsightUserId = this._authService.user.id;
            useDispatcher(this, clearState({}));
        }

        this._watchParams(coachingInsightUserId);
    }

    ngOnDestroy(): void {
        this._userSearchDialog.closeAll();
    }

    stateToProps(prevState?: RootState): void {
        if (prevState.user !== this.state.user) {
            this._userImage = !this.state.userImage
                ? null
                : this._domSanitizer.bypassSecurityTrustUrl(
                      this.state.userImage,
                  );
        }
    }

    /** @private */ _onKpiChange($event: SelectionChangeEvent): void {
        if (this.state.selectedPeriodType !== $event.periodType) {
            useDispatcher(
                this,
                selectPeriodType({ periodType: $event.periodType }),
            );
        }
        if (this.state.selectedKpi !== $event.kpi) {
            useDispatcher(this, selectKpi({ kpi: $event.kpi }));
        }
    }

    /** @private */ _onUserChange(userId: number): void {
        if (this.state.user.id !== userId) {
            this._reload(null, userId, null);
        }
    }

    /** @private */ _onOaUserChange(oaUser: FLOAPerson): void {
        this._oaUser = oaUser;
        sessionStorage.setItem(
            'coachingInsight-userId',
            this._oaUser.userId.toString(),
        );
    }

    /** @private */ _onMI2UserChange(oaUser: FLOAPerson): void {
        this._oaUser = oaUser;
    }

    onSelectUserClick($event: MouseEvent | KeyboardEvent): void {
        if (
            this._popUpService.active &&
            this._popUpService.active.componentRef.instance instanceof
                SearchUserContainerComponent
        ) {
            this._popUpService.hide();
            return;
        }
        this._userSearchDialog.openUsingPopUp({
            options: {
                target: () => $event.target as HTMLElement,
                placement: 'bottom-auto',
                autoClose: true,
                containerClass: 'cmps-widget-coaching-insight--pu-container',
            },
            userId: this.state.user.id,
            scope: SecurityScope.COACHING,
            close: (user) => {
                if (!user) {
                    return;
                }

                this._onUserChange(user.id);
            },
        });
    }

    private _reload(
        initUserId: number,
        userId: number,
        kpiTypeId: number,
    ): void {
        userId = !isNaN(userId) ? userId : initUserId;
        if (userId == null) {
            return;
        }

        kpiTypeId = !isNaN(kpiTypeId)
            ? kpiTypeId
            : this.state.selectedKpi.kpiTypeId;

        const userUnchanged = this.state.user && this.state.user.id === userId;
        const kpiUnchanged = this.state.selectedKpi.kpiTypeId === kpiTypeId;
        if (userUnchanged && kpiUnchanged) {
            return;
        }

        useDispatcher(
            this,
            reload({
                userId,
                periodTypeId: this.state.selectedPeriodType.periodTypeId,
                periodDate: this.state.selectedPeriodType.endDate,
                kpiTypeId,
            }),
        );

        useDispatcher(
            this,
            selectUser({
                userId,
                authUserId: this._authService.user.id,
            }),
        );
    }

    private _watchParams(initUserId: number): void {
        this._activatedRoute.queryParams
            .pipe(
                tap((params) => {
                    // eslint-disable-next-line @typescript-eslint/dot-notation
                    const userId = parseInt(params['userId']);
                    // eslint-disable-next-line @typescript-eslint/dot-notation
                    const kpiTypeId = parseInt(params['kpiTypeId']);

                    this._reload(initUserId, userId, kpiTypeId);

                    initUserId = null;
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
